// 遊戲 loading 畫面（Loading #2）— 忠實還原 Unity Slot026 LoadingUI 的**橫版與直版兩套 prefab**。
//
// Unity prefab 繼承鏈（同一批節點、兩套座標）：
//   橫版 SlotFramework/Prefab/UI/Loading/LoadingUI.prefab
//        → Slot026_RedRidingHood/Prefab/UI/Loading/Slot026_LoadingUI.prefab（Canvas 1920×1080 Expand）
//   直版 LoadingUI.prefab → LoadingUI_Portrait.prefab
//        → Slot026_LoadingUI_Portrait.prefab（Canvas 1080×1920 Expand）
// 兩個 Canvas 都是 ScaleWithScreenSize + Expand（UIRoot.prefab），切換由 LoadingResolutionManager
// 依 aspect>=1 改 planeDistance 完成 —— 也就是「直版不是橫版縮小，而是另一套版面」。
//
// 版面（Unity RectTransform，中心原點、y 向上；本檔子節點局部座標 = (unityX, −unityY)，見 LAYOUT）：
//   背景 bg_loading（獨立 cover 填滿螢幕，不隨設計容器縮放）
//   角色主視覺 bg_loadingCharacter — **只有橫版有**（直版 prefab 已 m_RemovedGameObjects 移除該節點）
//   頂部 遊戲標題 logo（Art/Game/<lang>/logo.png）
//   中央 圖文輪播 ScrollView/Panel：
//     ├ PrevButton / NextButton（btn_scoreboard.png 金色三角，Prev 以 localScale.x 負值鏡像）
//     ├ Blur（pic_blur 暈光底）
//     ├ ScrollContentRoot（RectMask2D 664×498）內三頁示意圖，左右滑動切頁
//     └ ScrollContentTextRoot 內三則提示文字 game.Loading_1~3（Unity 為 enabled 切換、無淡入淡出）
//   下方 進度條群組（pic_barBg 左右鏡像兩片 + pic_bar SlicedFilledImage 左→右 + 百分比文字）
//   同位置 Continue 按鈕 btn_win（初始隱藏，載入完成後隱藏進度條、淡入本鈕）
//   畫面底緣 深色條 pic_loadDark ＋ combo 品牌 logo、左下角版本號
//     ⚠️ 這三者在 Unity 錨在**畫面邊緣**（Bottom 的 anchor.y=0、VersionText 的 anchor=(0,0)），
//        Expand 下 Canvas 實際 rect 會比參考解析度大，故必須釘視窗邊緣而非設計框邊緣（見 bottomLayer）。
//
// 進度：WebGL 起始 INITIAL_PROGRESS=0.5（combo 疊層負責前半、遊戲 loading 負責後半）。
// 輪播：LoadingUIMediator._changeDuration=5s 自動下一頁、_scrollSwitchDuration=0.25s 滑動（ease-out cubic）。
// [已知未移植] LoadingBar 群組內的 ProgressInfoText（Unity dev build 才會顯示的階段名稱，如 "ChangeGameScene"）。

import { Container, Graphics, NineSliceSprite, Rectangle, Sprite, Text } from 'pixi.js'
import gsap from 'gsap'
import type { Orientation, Resizable } from '../../framework/render/design'
import { fontFamilyForLocale, t } from '../../framework/i18n/i18n'
import { getLoadingTexture } from '../assets/LoadingAssets'
import { BitmapNumber } from './BitmapNumber'
import { isNumberFontLoaded, type NumberFontName } from '../assets/NumberFont'
import { killTweensOf, tweenTo } from './tween'

/** WebGL 起始進度（對照 Unity LoadingUI.INITIAL_PROGRESS=0.5；combo 疊層已完成前半）。 */
const INITIAL_PROGRESS = 0.5

/**
 * pic_bar 的 9-slice 邊界：Unity `pic_bar.png.meta` 的 `spriteBorder: {x:22, y:0, z:22, w:0}`
 * ——左右各 22px 圓角端帽固定不拉伸、僅中段（94−44=50px）橫向延展；上下無邊界。
 * 貼圖原生僅 94×48，若當一般 Sprite 直接拉到約 973 寬（約 10.35×），端帽與深色描邊會被
 * 橫向抹成長條（先前的破圖成因）。故用 NineSliceSprite 還原 Unity SlicedFilledImage 的
 * 「維持 9-slice 幾何、再依進度橫向裁切」行為。
 */
const FILL_SLICE = 22

/** 輪播切換間隔（Unity LoadingUIMediator._changeDuration=5s）與滑動時間（_scrollSwitchDuration=0.25s）。 */
const TIP_INTERVAL_MS = 5000
const TIP_SLIDE_S = 0.25

/** ScrollView/Panel 與 ScrollContentRoot 的 rect（Unity 兩者皆 664×498；滑動位移量＝rect.width）。 */
const PAGE_W = 664
const PAGE_H = 498

/** Bottom 深色條（pic_loadDark）sizeDelta；pivot(0.5,0) 釘畫面底緣。 */
const BOTTOM_W = 2520
const BOTTOM_H = 224
/**
 * combo 品牌 logo：Unity sizeDelta 只是編輯期值，LoadingUI.SetBrandLogo() 換圖後會呼叫 SetNativeSize()，
 * 執行期實際尺寸＝logo_combo 原生 186×53。
 */
const BRAND_W = 186
const BRAND_H = 53

/** VersionText：anchor/pivot (0,0)＝畫面左下角 + (10,5)，TMP fontSize 36。 */
const VERSION_LABEL = 'v.0.1.23.pixi'
const VERSION_MARGIN_X = 10
const VERSION_MARGIN_Y = 5
const VERSION_FONT = 36

/** 提示文字 TMP：fontSize 40、Bold、置中、m_characterSpacing −4（＝ −4×0.01×40 = −1.6 設計 px）。 */
const TIP_FONT = 40
const TIP_LETTER_SPACING = -1.6
const TIP_WRAP_W = 720

/** 百分比文字 TMP fontSize 36。 */
const PERCENT_FONT = 36
/** Continue 按鈕底圖 sizeDelta 384×136、內文 TMP fontSize 50（整顆再乘 localScale）。 */
const CONTINUE_W = 384
const CONTINUE_H = 136
const CONTINUE_FONT = 50

// ── 最高贏分頁（pic_load_all）上的「30,000X」 ──
//
// Unity MaxWinNumText 是 TMP「空字型 Empty.asset ＋ TMP_SpriteAsset 點陣字圖」的慣用組合
// （同 WinTemp 的作法），故用 BitmapNumber 逐字排點陣 glyph，而不是系統字（先前用純白系統字＝字型異常成因）。
// sprite asset ＝ SlotFramework/Art/Font/Common/**tx_loading**（614×546、13 個 glyph、高一律 182），
// ⚠️ 不是 tx_multiple／tx_marquee；三者同資料夾但長相不同。
// TMP 元件序列化值：fontSize 200（autoSize 18~200）、m_characterSpacing −10、白色無漸層無描邊、
// 水平置中 + **垂直 Geometry（幾何中心）對齊**，RectTransform 為滿版 stretch 於 Image_MaxWin 的
// 664×498、m_margin 全 0（Slot026 Variant 只覆寫 m_text=30,000X 與 m_fontSize=200，未動對齊）。
// ⚠️ m_VerticalAlignment 的 4096 是 Geometry、**1024 才是 Bottom**（TMP_Text.cs:83 的
// VerticalAlignmentOptions）；先前誤讀成「垂直靠下」而把整串壓到頁面底部（＝使用者回報的位置太下面）。
const MAX_WIN_TEXT = '30,000X'
const MAX_WIN_FONT: NumberFontName = 'loading'
const MAX_WIN_TMP_FONT_SIZE = 200
/**
 * Empty.asset：pointSize 71、ascentLine 64.274414、scale 1。
 * tx_loading.asset 的 m_FaceInfo.m_PointSize 為 0，故 TMP 走「sprite 高正規化到 fontAsset.ascentLine」
 * 那條（currentElementScale = ascentLine / sprite.height × fontSize / pointSize）
 * → 顯示字高＝fontSize × 此比例，與 sprite 原生 182px 無關。
 */
const EMPTY_FONT_ASCENT_RATIO = 64.274414 / 71
/** m_characterSpacing −10 → 每字間距 ＝ −10 × 0.01 × fontSize（設計 px）。 */
const MAX_WIN_CHAR_SPACING = -10 * 0.01

/** 一套版型的 Unity 節點座標（anchoredPosition/sizeDelta/localScale 原值）。 */
interface LoadingLayout {
  /** Canvas 參考解析度（Expand 縮放基準）。 */
  readonly refW: number
  readonly refH: number
  /** 角色主視覺（直版 prefab 無此節點 → null）。 */
  readonly character: { readonly ax: number; readonly ay: number; readonly w: number; readonly h: number } | null
  readonly logo: { readonly ax: number; readonly ay: number; readonly scale: number }
  /** ScrollView 位置與 Panel 的 localScale。 */
  readonly scroll: { readonly ax: number; readonly ay: number; readonly panelScale: number }
  readonly prev: { readonly ax: number; readonly ay: number; readonly w: number; readonly h: number; readonly scale: number }
  readonly next: { readonly ax: number; readonly ay: number; readonly w: number; readonly h: number; readonly scale: number }
  readonly blur: { readonly ax: number; readonly ay: number; readonly w: number; readonly h: number }
  /** ScrollContentRoot 的 localScale 與各頁 Image 的 localScale。 */
  readonly pages: { readonly rootScale: number; readonly pageScale: number }
  /** ScrollText1~3 的 anchoredPosition.y。 */
  readonly tipAy: number
  readonly bar: {
    readonly ay: number
    readonly bgW: number
    readonly bgH: number
    readonly bgAy: number
    readonly fillW: number
    readonly fillH: number
    readonly fillAx: number
    readonly fillAy: number
  }
  readonly continueBtn: { readonly ax: number; readonly ay: number; readonly scale: number }
  /** combo logo：x 為 Bottom 內的 anchoredPosition.x，uy 為「距畫面底緣」的設計 px。 */
  readonly brand: { readonly ax: number; readonly uy: number }
}

/** 橫版 Slot026_LoadingUI.prefab（Canvas 1920×1080）。 */
const LANDSCAPE: LoadingLayout = {
  refW: 1920,
  refH: 1080,
  character: { ax: -458.6, ay: -153.9, w: 793, h: 1103 },
  logo: { ax: -1.8, ay: 424.5, scale: 1.29 },
  scroll: { ax: 505, ay: 100, panelScale: 1 },
  prev: { ax: -376, ay: -34.559288, w: 84.3014, h: 173.9754, scale: 1 },
  next: { ax: 374, ay: -31.6086, w: 85.4163, h: 158.2833, scale: 1 },
  blur: { ax: 0, ay: -330, w: 928, h: 207 },
  pages: { rootScale: 1, pageScale: 1 },
  tipAy: -330,
  bar: { ay: -370, bgW: 512.8286, bgH: 118.408, bgAy: 0.3163, fillW: 972.9445, fillH: 46.5476, fillAx: 0.4609, fillAy: 3.2016 },
  continueBtn: { ax: 511, ay: -365, scale: 0.8 },
  // Bottom（pivot 0.5,0、高 224）中心距畫面底緣 112；BrandLogo 在 Bottom 內 aPos (0.8821, −44.8)。
  brand: { ax: 0.8821, uy: 112 - 44.8 },
}

/** 直版 Slot026_LoadingUI_Portrait.prefab（Canvas 1080×1920）。 */
const PORTRAIT: LoadingLayout = {
  refW: 1080,
  refH: 1920,
  character: null, // LoadingUI_Portrait.prefab 的 m_RemovedGameObjects 已移除角色節點
  logo: { ax: -0.5, ay: 735.7, scale: 1.54 },
  scroll: { ax: 0, ay: 125, panelScale: 1.2 },
  prev: { ax: -386, ay: -3.8, w: 84, h: 158, scale: 1.1 },
  next: { ax: 380, ay: -3.5, w: 84, h: 158, scale: 1.1 },
  blur: { ax: 0, ay: -470, w: 928, h: 208 },
  pages: { rootScale: 1.28, pageScale: 0.8 },
  tipAy: -400,
  bar: { ay: -660, bgW: 512.8286, bgH: 118, bgAy: 0, fillW: 972.9445, fillH: 46, fillAx: -0.0911, fillAy: 0.6172 },
  continueBtn: { ax: 0, ay: -660, scale: 1 },
  brand: { ax: 1.1585, uy: 112 - 39 },
}

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v))

export class LoadingView extends Container implements Resizable {
  /** 背景（獨立 cover，不隨設計容器縮放）。 */
  private readonly bg = new Sprite(getLoadingTexture('bg'))
  /** 中心錨定的設計座標層（Expand 縮放、置中螢幕）。 */
  private readonly content = new Container()
  /** 畫面底緣錨定層（Bottom 深色條／combo logo／版本號；Expand 下不可跟著設計框走）。 */
  private readonly bottomLayer = new Container()

  private readonly character = new Sprite(getLoadingTexture('character'))
  private readonly gameLogo = new Sprite(getLoadingTexture('gameLogo'))
  private readonly bottomBar = new Sprite(getLoadingTexture('bottomBar'))
  private readonly brandLogo = new Sprite(getLoadingTexture('brandLogo'))
  private readonly version = new Text({
    text: VERSION_LABEL,
    style: { fontFamily: fontFamilyForLocale(), fontSize: VERSION_FONT, fill: 0xffffff, align: 'left' },
  })

  // ── 輪播（ScrollView → Panel → PrevButton/NextButton/Blur/ScrollContentRoot/ScrollContentTextRoot） ──
  private readonly scrollView = new Container()
  private readonly panel = new Container()
  private readonly prevButton = new Container()
  private readonly nextButton = new Container()
  private readonly prevSprite = new Sprite(getLoadingTexture('arrow'))
  private readonly nextSprite = new Sprite(getLoadingTexture('arrow'))
  private readonly blur = new Sprite(getLoadingTexture('blur'))
  private readonly scrollRoot = new Container()
  private readonly scrollMask = new Graphics()
  private readonly pages: Container[] = []
  private readonly pageImages: Sprite[] = []
  private readonly tips: Text[] = []
  private readonly maxWinNumber = new BitmapNumber(MAX_WIN_FONT)
  private tipIndex = 0
  private tipTimer = 0
  /** 滑動中：Unity 於動畫期間把兩顆箭頭 interactable=false。 */
  private switching = false
  /** 滑動進度 proxy（0→1），同時推動離場與進場兩張圖。 */
  private readonly slide = { p: 0 }

  // ── 進度條 / Continue ──
  private readonly barGroup = new Container()
  private readonly barBgLeft = new Sprite(getLoadingTexture('barBg'))
  private readonly barBgRight = new Sprite(getLoadingTexture('barBg'))
  private readonly fill = new NineSliceSprite({
    texture: getLoadingTexture('barFill'),
    leftWidth: FILL_SLICE,
    rightWidth: FILL_SLICE,
    topHeight: 0,
    bottomHeight: 0,
  })
  private readonly fillMask = new Graphics()
  private readonly percent = new Text({
    text: '',
    style: { fontFamily: fontFamilyForLocale(), fontSize: PERCENT_FONT, fill: 0xffffff, align: 'center' },
  })
  private readonly continueGroup = new Container()
  private readonly continueBtn = new Sprite(getLoadingTexture('continueBtn'))
  private readonly continueLabel = new Text({
    text: t('common.Btn_Start'),
    style: { fontFamily: fontFamilyForLocale(), fontSize: CONTINUE_FONT, fill: 0xffffff, fontWeight: 'bold', align: 'center' },
  })

  /** 目前套用的版型（resize 決定）。 */
  private layout: LoadingLayout = LANDSCAPE
  /** 進度顯示值（0..1，INITIAL_PROGRESS 起）；由 gsap 平滑補間，onUpdate 重繪填充與百分比。 */
  private readonly barProxy = { v: INITIAL_PROGRESS }

  constructor() {
    super()
    this.addChild(this.bg, this.content, this.bottomLayer)
    this.bg.anchor.set(0.5)

    this.buildContent()
    this.buildScrollArea()
    this.buildProgressBar()
    this.buildBottomLayer()

    this.redrawBar()
    this.startTipRotation()
  }

  // ── 建構（座標於 resize 時依版型套用） ──

  private buildContent(): void {
    this.character.anchor.set(0.5)
    this.gameLogo.anchor.set(0.5)
    this.content.addChild(this.character, this.gameLogo, this.scrollView, this.barGroup, this.continueGroup)
  }

  /** 中央圖文輪播：暈光底 + 三頁示意圖（滑動切換、RectMask2D 裁切）+ 三則提示文字 + 左右箭頭。 */
  private buildScrollArea(): void {
    this.blur.anchor.set(0.5)

    // 三頁示意圖（Unity ScrollContentRoot 的三個子物件；第三頁疊「30,000X」）。
    const pageKeys = ['page1', 'page2', 'page3'] as const
    for (const key of pageKeys) {
      const page = new Container()
      const img = new Sprite(getLoadingTexture(key))
      img.anchor.set(0.5)
      page.addChild(img)
      if (key === 'page3') page.addChild(this.maxWinNumber)
      this.pageImages.push(img)
      this.pages.push(page)
      this.scrollRoot.addChild(page)
    }
    this.maxWinNumber.setText(MAX_WIN_TEXT)
    this.scrollRoot.mask = this.scrollMask

    // 三則提示文字（game.Loading_1~3）；Unity 以 enabled 切換、無淡入淡出。
    const tipKeys = ['game.Loading_1', 'game.Loading_2', 'game.Loading_3']
    for (let i = 0; i < tipKeys.length; i++) {
      const tip = new Text({
        text: t(tipKeys[i]!),
        style: {
          fontFamily: fontFamilyForLocale(),
          fontSize: TIP_FONT,
          fill: 0xffffff,
          fontWeight: 'bold',
          align: 'center',
          letterSpacing: TIP_LETTER_SPACING,
          wordWrap: true,
          wordWrapWidth: TIP_WRAP_W,
          // 中文/日文/泰文沒有空白可斷行，不開 breakWords 就整句不折、會撐出 720 寬的框
          // （Unity TMP 對 CJK 本來就逐字斷行）。
          breakWords: true,
        },
      })
      tip.anchor.set(0.5)
      tip.visible = i === 0
      this.tips.push(tip)
      this.panel.addChild(tip)
    }

    // 左右箭頭（btn_scoreboard.png；Prev 由 localScale.x 負值鏡像成朝左）。
    this.setupArrow(this.prevButton, this.prevSprite, () => this.switchTip(false))
    this.setupArrow(this.nextButton, this.nextSprite, () => this.switchTip(true))

    this.panel.addChild(this.scrollMask, this.scrollRoot, this.blur, this.prevButton, this.nextButton)
    this.scrollView.addChild(this.panel)
    // Unity 階層順序：Prev/Next → Blur → ScrollContentRoot → ScrollContentTextRoot（後者最上層）。
    this.panel.setChildIndex(this.blur, 0)
    for (const tip of this.tips) this.panel.setChildIndex(tip, this.panel.children.length - 1)
  }

  private setupArrow(button: Container, sprite: Sprite, onTap: () => void): void {
    sprite.anchor.set(0.5)
    button.addChild(sprite)
    button.eventMode = 'static'
    button.cursor = 'pointer'
    button.on('pointertap', () => {
      if (this.switching) return
      onTap()
    })
  }

  /** 進度條群組：底圖左右鏡像兩片 + 填充（左→右）+ 百分比文字；同位置疊 Continue 按鈕。 */
  private buildProgressBar(): void {
    // Unity LoadingBarBG_0/1：pivot (1,0.5)，BG_1 再以 localScale.x=−1 鏡像，合成對稱底條。
    this.barBgLeft.anchor.set(1, 0.5)
    this.barBgRight.anchor.set(1, 0.5)
    this.fill.mask = this.fillMask
    this.percent.anchor.set(0.5)
    this.barGroup.addChild(this.barBgLeft, this.barBgRight, this.fill, this.fillMask, this.percent)

    this.continueBtn.anchor.set(0.5)
    this.continueLabel.anchor.set(0.5)
    this.continueGroup.addChild(this.continueBtn, this.continueLabel)
    this.continueGroup.alpha = 0
  }

  /** 畫面底緣層：深色條（pivot 底緣中心）＋ combo logo ＋ 左下角版本號。 */
  private buildBottomLayer(): void {
    this.bottomBar.anchor.set(0.5, 1)
    this.brandLogo.anchor.set(0.5)
    this.brandLogo.setSize(BRAND_W, BRAND_H)
    this.version.anchor.set(0, 1)
    this.version.alpha = 0.7
    this.bottomLayer.addChild(this.bottomBar, this.brandLogo, this.version)
  }

  // ── 進度 / 輪播 / 進場收尾 ──

  /** 設定載入進度（0..1，遊戲 loading 階段）；顯示值自 INITIAL_PROGRESS 平滑補到 1（對照 Unity ease-out）。 */
  setProgress(progress: number): void {
    const target = INITIAL_PROGRESS + (1 - INITIAL_PROGRESS) * clamp01(progress)
    gsap.to(this.barProxy, {
      // Unity LoadingUI.SetLoadingBarProgress：100% 用 0.1s、其餘 0.5s，緩動 1−(1−t)³＝ease-out cubic。
      v: target,
      duration: target >= 1 ? 0.1 : 0.5,
      ease: 'power3.out',
      // 進度改為位元組串流回報後會被頻繁重新設定目標值（見 main.ts）；不覆寫舊補間會讓多個
      // 補間同時搶改 barProxy.v 而抖動甚至倒退。overwrite 讓新目標接續當前顯示值繼續前進。
      overwrite: 'auto',
      onUpdate: () => this.redrawBar(),
    })
  }

  /** 依 barProxy.v 重繪填充遮罩（左→右）與百分比文字。 */
  private redrawBar(): void {
    const v = clamp01(this.barProxy.v)
    const { fillW, fillH, fillAx, fillAy } = this.layout.bar
    const left = fillAx - fillW / 2
    const top = -fillAy - fillH / 2
    this.fillMask.clear().rect(left, top, Math.max(0, fillW * v), fillH).fill(0xffffff)
    this.percent.text = `${Math.round(v * 100)}%`
  }

  /** 啟動自動輪播（Unity LoadingUIMediator.Update：每 _changeDuration 秒切下一頁）。 */
  private startTipRotation(): void {
    this.tipTimer = window.setInterval(() => this.switchTip(true), TIP_INTERVAL_MS)
  }

  /**
   * 切換到上/下一頁：圖片左右滑動（Unity LoadingUI.ScrollContentSwitch，ease-out cubic），
   * 文字直接切換 visible（Unity SetContentTextActive 只改 enabled）。
   */
  private switchTip(next: boolean): void {
    if (this.switching || this.pages.length < 2) return
    const from = this.tipIndex
    const to = (from + (next ? 1 : -1) + this.pages.length) % this.pages.length
    const outgoing = this.pages[from]
    const incoming = this.pages[to]
    if (outgoing === undefined || incoming === undefined) return

    this.switching = true
    this.tipIndex = to
    for (let i = 0; i < this.tips.length; i++) {
      const tip = this.tips[i]
      if (tip !== undefined) tip.visible = i === to
    }
    // 自動輪播計時歸零（對照 Unity SetScrollContentSwitch 的 _tempTime = 0）。
    this.restartTipTimer()

    // 兩張圖以「單一進度 proxy」同步位移（對照 Unity ScrollContentSwitch 用同一個 t 推兩張圖）：
    // 一條 tween 一個 onComplete，不會出現「其中一條被覆寫掉，switching 永遠卡住」的情況。
    const dir = next ? 1 : -1
    incoming.x = dir * PAGE_W
    this.slide.p = 0
    killTweensOf(this.slide)
    gsap.to(this.slide, {
      p: 1,
      duration: TIP_SLIDE_S,
      ease: 'power3.out',
      onUpdate: () => {
        const p = this.slide.p
        incoming.x = dir * PAGE_W * (1 - p)
        outgoing.x = -dir * PAGE_W * p
      },
      onComplete: () => {
        incoming.x = 0
        outgoing.x = -dir * PAGE_W
        this.switching = false
      },
    })
  }

  private restartTipTimer(): void {
    if (this.tipTimer !== 0) window.clearInterval(this.tipTimer)
    this.startTipRotation()
  }

  /**
   * 載入完成呈現：隱藏進度條群組、淡入 Continue 按鈕，等待玩家點擊後 resolve。
   * 對照 Unity ShowContinueButton（_loadingBarRoot 隱藏、_continueButtonCanvasGroup alpha 0→1）＋ UILoadingContinueClickEvent。
   */
  async awaitContinue(): Promise<void> {
    this.setProgress(1)
    this.barGroup.visible = false
    this.continueGroup.eventMode = 'static'
    this.continueGroup.cursor = 'pointer'
    await tweenTo(this.continueGroup, { alpha: 1, duration: 0.3 })
    await new Promise<void>((resolve) => {
      this.continueGroup.once('pointertap', () => resolve())
    })
  }

  // ── 版面 ──

  resize(width: number, height: number, orientation: Orientation): void {
    if (width === 0 || height === 0) return
    const spec = orientation === 'portrait' ? PORTRAIT : LANDSCAPE
    this.layout = spec

    // 背景：cover 填滿螢幕（保持長寬比、溢出裁切），置中。
    // （Unity 為固定 1260×1260 ×scale2 置中的 Simple Image；cover 是同視覺、且對極端長寬比更安全的等價作法。）
    const cover = Math.max(width / this.bg.texture.width, height / this.bg.texture.height)
    this.bg.scale.set(cover)
    this.bg.position.set(width / 2, height / 2)

    // Unity CanvasScaler ScaleWithScreenSize + Expand → 縮放係數取 min（整個參考解析度都看得到）。
    const s = Math.min(width / spec.refW, height / spec.refH)
    this.content.scale.set(s)
    this.content.position.set(width / 2, height / 2)
    this.bottomLayer.scale.set(s)
    this.bottomLayer.position.set(width / 2, height)

    this.layoutContent(spec)
    this.layoutScrollArea(spec)
    this.layoutBar(spec)
    this.layoutBottom(width, s)
    this.redrawBar()
  }

  private layoutContent(spec: LoadingLayout): void {
    if (spec.character === null) {
      this.character.visible = false
    } else {
      this.character.visible = true
      this.character.setSize(spec.character.w, spec.character.h)
      this.character.position.set(spec.character.ax, -spec.character.ay)
    }

    // Unity Logo 掛 LocalizeSpriteEvent，執行期換圖後 SetNativeSize()，故用貼圖原生尺寸 × localScale。
    this.gameLogo.scale.set(spec.logo.scale)
    this.gameLogo.position.set(spec.logo.ax, -spec.logo.ay)

    this.continueGroup.scale.set(spec.continueBtn.scale)
    this.continueGroup.position.set(spec.continueBtn.ax, -spec.continueBtn.ay)
    this.continueBtn.setSize(CONTINUE_W, CONTINUE_H)
  }

  private layoutScrollArea(spec: LoadingLayout): void {
    this.scrollView.position.set(spec.scroll.ax, -spec.scroll.ay)
    this.panel.scale.set(spec.scroll.panelScale)

    this.blur.setSize(spec.blur.w, spec.blur.h)
    this.blur.position.set(spec.blur.ax, -spec.blur.ay)

    layoutArrow(this.prevButton, this.prevSprite, spec.prev, -1)
    layoutArrow(this.nextButton, this.nextSprite, spec.next, 1)

    // ScrollContentRoot：localScale 縮放，RectMask2D 裁切自身 rect（664×498，Panel 局部座標）。
    const root = spec.pages.rootScale
    this.scrollRoot.scale.set(root)
    this.scrollMask
      .clear()
      .rect((-PAGE_W / 2) * root, (-PAGE_H / 2) * root, PAGE_W * root, PAGE_H * root)
      .fill(0xffffff)

    for (let i = 0; i < this.pages.length; i++) {
      const page = this.pages[i]
      const img = this.pageImages[i]
      if (page === undefined || img === undefined) continue
      page.scale.set(spec.pages.pageScale)
      // 目前這頁在中央，其餘停在遮罩外（Unity 初始 Image=0、其餘=664）。
      page.x = i === this.tipIndex ? 0 : PAGE_W
      page.y = 0
      img.setSize(PAGE_W, PAGE_H)
    }
    this.layoutMaxWinNumber()

    for (const tip of this.tips) tip.position.set(0, -spec.tipAy)
  }

  /**
   * 「30,000X」：先以 fontSize 200 排版，超過 rect 寬時等比縮小（＝TMP autoSize 18~200 的等價行為，
   * 因 glyph 高／字距／字寬全部與 fontSize 成正比，量一次即可一次縮到位）；
   * 垂直則照 TMP 的 Geometry 對齊落在 rect 正中央。
   */
  private layoutMaxWinNumber(): void {
    if (!isNumberFontLoaded(MAX_WIN_FONT)) {
      this.maxWinNumber.visible = false
      return
    }
    this.maxWinNumber.visible = true
    // 字距比例相對字高，與 fontSize 無關 → 只需設一次。
    const ratio = MAX_WIN_CHAR_SPACING / EMPTY_FONT_ASCENT_RATIO
    this.maxWinNumber.setLetterSpacing(ratio)
    const fullHeight = MAX_WIN_TMP_FONT_SIZE * EMPTY_FONT_ASCENT_RATIO
    this.maxWinNumber.setHeight(fullHeight)
    const shrink = Math.min(1, PAGE_W / this.maxWinNumber.contentWidth)
    if (shrink < 1) this.maxWinNumber.setHeight(fullHeight * shrink)

    // 垂直 Geometry 對齊（m_VerticalAlignment 4096）＝ TMP 3.0.9 TMPro_UGUI_Private.cs:3605：
    //   anchorOffset.y = rect 垂直中心 − (meshExtents.max.y + meshExtents.min.y) / 2   （margin 全 0）
    // 也就是「可見 quad 的垂直中心貼齊 rect 中心」，與 baseline／ascent／descent 全然無關。
    // tx_loading 的 13 個 glyph 全部 m_Height=182、m_HorizontalBearingY=0（tx_loading.asset）→ quad 垂直
    // 完全重合；BitmapNumber 亦是每個 glyph 以 anchor.y=0.5 對齊本地原點，故直接放 rect 中心即等價。
    this.maxWinNumber.position.set(0, 0)
  }

  private layoutBar(spec: LoadingLayout): void {
    const bar = spec.bar
    this.barGroup.position.set(0, -bar.ay)

    for (const bg of [this.barBgLeft, this.barBgRight]) {
      bg.setSize(bar.bgW, bar.bgH)
      bg.position.set(0, -bar.bgAy)
    }
    // BG_1 以 localScale.x=−1 水平鏡像成右半邊。
    // ⚠️ PixiJS 的 Sprite.setSize 會保留 scale 的正負號，所以這裡要直接指定負值（不可寫 *= -1，會逐次翻回）。
    this.barBgRight.scale.x = -Math.abs(this.barBgRight.scale.x)

    this.fill.width = bar.fillW
    this.fill.height = bar.fillH
    this.fill.position.set(bar.fillAx - bar.fillW / 2, -bar.fillAy - bar.fillH / 2)
  }

  /** 底緣層：深色條釘視窗底緣、combo logo 於其上、版本號釘視窗左下角。 */
  private layoutBottom(width: number, s: number): void {
    this.bottomBar.setSize(BOTTOM_W, BOTTOM_H)
    this.bottomBar.position.set(0, 0)
    this.brandLogo.position.set(this.layout.brand.ax, -this.layout.brand.uy)
    // 版本號 anchor/pivot (0,0)＝視窗左下角；bottomLayer 原點在底緣中央，故換算回視窗左緣。
    this.version.position.set(-width / (2 * s) + VERSION_MARGIN_X, -VERSION_MARGIN_Y)
  }

  /** 淡出並銷毀（Continue 之後由 main.ts 呼叫；Unity 為直接 Destroy，本移植加淡出求平滑）。 */
  async fadeOutAndDestroy(): Promise<void> {
    killTweensOf(this)
    await tweenTo(this, { alpha: 0, duration: 0.4 })
    if (!this.destroyed) this.destroy({ children: true })
  }

  override destroy(options?: Parameters<Container['destroy']>[0]): void {
    if (this.tipTimer !== 0) {
      window.clearInterval(this.tipTimer)
      this.tipTimer = 0
    }
    killTweensOf(this.barProxy)
    killTweensOf(this.slide)
    killTweensOf(this.continueGroup)
    killTweensOf(this)
    super.destroy(options)
  }
}

/** 箭頭：sizeDelta 決定貼圖尺寸、localScale 決定整顆縮放與左右鏡像（Prev 為 x 負值）。 */
function layoutArrow(
  button: Container,
  sprite: Sprite,
  spec: { readonly ax: number; readonly ay: number; readonly w: number; readonly h: number; readonly scale: number },
  signX: number,
): void {
  sprite.setSize(spec.w, spec.h)
  button.scale.set(spec.scale * signX, spec.scale)
  button.position.set(spec.ax, -spec.ay)
  button.hitArea = new Rectangle(-spec.w / 2, -spec.h / 2, spec.w, spec.h)
}
